/**
 * Wagmi connector factory for 1auth passkey authentication.
 *
 * Wraps `createOneAuthProvider` in a wagmi-compatible connector so that
 * passkey-based accounts can participate in the full wagmi ecosystem:
 * `useAccount`, `useConnect`, `useSendTransaction`, hooks, and so on.
 *
 * Import path: `@rhinestone/1auth/wagmi`
 *
 * @module
 */

import { createConnector, type Connector } from "@wagmi/core";
import {
  numberToHex,
  type Address,
  type Chain,
  type ProviderConnectInfo,
} from "viem";
import { createOneAuthProvider, type OneAuthProvider } from "./provider";
import type { OneAuthClient } from "./client";

export type OneAuthConnectorOptions = {
  client: OneAuthClient;
  chainId?: number;
  storageKey?: string;
  waitForHash?: boolean;
  hashTimeoutMs?: number;
  hashIntervalMs?: number;
};

/**
 * Creates a wagmi connector backed by 1auth passkey authentication.
 *
 * The connector lazily instantiates a single `OneAuthProvider` on the first call to
 * `getProvider()` and reuses it for the lifetime of the wagmi config. Event listeners
 * (`accountsChanged`, `chainChanged`, `disconnect`) are attached on connect and torn
 * down on disconnect to prevent memory leaks.
 *
 * @param options - Connector configuration
 * @param options.client - Configured `OneAuthClient` instance
 * @param options.chainId - Override the initial chain ID. When omitted the first chain
 *   in the wagmi config's `chains` array is used.
 * @param options.storageKey - localStorage key for persisting the connected user.
 *   Forwarded to `createOneAuthProvider`. Defaults to `"1auth-user"`.
 * @param options.waitForHash - When true, `sendTransaction` waits for a tx hash before
 *   resolving. Forwarded to `createOneAuthProvider`. Defaults to true.
 * @param options.hashTimeoutMs - Maximum milliseconds to wait for a transaction hash.
 * @param options.hashIntervalMs - Polling interval when waiting for a hash.
 * @returns A wagmi connector factory function (pass directly to wagmi `createConfig`)
 *
 * @example
 * ```typescript
 * import { createConfig } from "wagmi";
 * import { base, optimism } from "viem/chains";
 * import { oneAuth } from "@rhinestone/1auth/wagmi";
 * import { OneAuthClient } from "@rhinestone/1auth";
 *
 * const client = new OneAuthClient({ clientId: "my-app" });
 *
 * export const wagmiConfig = createConfig({
 *   chains: [base, optimism],
 *   connectors: [
 *     oneAuth({ client }),
 *   ],
 * });
 * ```
 */
export function oneAuth(options: OneAuthConnectorOptions) {
  type Provider = OneAuthProvider;

  return createConnector<Provider>((wagmiConfig) => {
    const chains = (wagmiConfig.chains ?? []) as readonly [Chain, ...Chain[]];
    const initialChainId = options.chainId ?? chains[0]?.id;

    let provider: Provider | null = null;
    // Bound listener references held so they can be removed by name during disconnect
    let accountsChanged: Connector["onAccountsChanged"] | undefined;
    let chainChanged: Connector["onChainChanged"] | undefined;
    let connect: Connector["onConnect"] | undefined;
    let disconnect: Connector["onDisconnect"] | undefined;

    return {
      id: "1auth",
      name: "1auth Passkey",
      type: "wallet",

      /**
       * Connects the user and registers provider event listeners.
       *
       * On reconnect (`isReconnecting: true`) attempts to restore accounts from
       * localStorage silently before falling back to the connect modal. Switches to
       * the requested `chainId` if it differs from the provider's current chain.
       * Removes the one-time `connect` listener after the first connection so it is
       * not triggered again on subsequent reconnects.
       *
       * @param params.chainId - Optional chain to switch to after connecting
       * @param params.isReconnecting - True when wagmi is restoring a previous session
       * @param params.withCapabilities - When true, returns accounts with capability maps
       * @returns Connected accounts and the active chain ID
       * @throws If no chain is configured or the user cancels the auth flow
       */
      async connect<withCapabilities extends boolean = false>(
        { chainId, isReconnecting, withCapabilities }:
          | {
              chainId?: number;
              isReconnecting?: boolean;
              withCapabilities?: withCapabilities | boolean;
            }
          | undefined = {}
      ): Promise<{
        accounts: withCapabilities extends true
          ? readonly { address: Address; capabilities: Record<string, unknown> }[]
          : readonly Address[];
        chainId: number;
      }> {
        if (!initialChainId) {
          throw new Error("No chain configured for 1auth connector");
        }

        const provider = await this.getProvider({ chainId });
        let accounts: readonly Address[] = [];
        let currentChainId = await this.getChainId();

        if (isReconnecting) {
          accounts = await this.getAccounts().catch(() => []);
        }

        if (!accounts.length) {
          accounts = (await provider.request({
            method: "wallet_connect",
          })) as Address[];
          currentChainId = await this.getChainId();
        }

        if (chainId && currentChainId !== chainId) {
          await provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: numberToHex(chainId) }],
          });
          currentChainId = await this.getChainId();
        }

        // Remove the one-time connect listener now that connection is established
        if (connect) {
          provider.removeListener("connect", connect as never);
          connect = undefined;
        }
        if (!accountsChanged) {
          accountsChanged = this.onAccountsChanged.bind(this);
          provider.on("accountsChanged", accountsChanged as never);
        }
        if (!chainChanged) {
          chainChanged = this.onChainChanged.bind(this);
          provider.on("chainChanged", chainChanged as never);
        }
        if (!disconnect) {
          disconnect = this.onDisconnect.bind(this);
          provider.on("disconnect", disconnect as never);
        }

        const response = withCapabilities
          ? {
              accounts: accounts.map((account) => ({
                address: account,
                capabilities: {},
              })),
              chainId: currentChainId,
            }
          : {
              accounts,
              chainId: currentChainId,
            };

        return response as unknown as {
          accounts: withCapabilities extends true
            ? readonly { address: Address; capabilities: Record<string, unknown> }[]
            : readonly Address[];
          chainId: number;
        };
      },

      /**
       * Disconnects the user and removes `chainChanged` / `disconnect` event listeners.
       *
       * The `accountsChanged` listener is intentionally left attached so that wagmi
       * receives the empty-accounts event emitted by the underlying provider's
       * `clearStoredUser` path.
       */
      async disconnect() {
        const provider = await this.getProvider();
        await provider.disconnect();
        if (chainChanged) {
          provider.removeListener("chainChanged", chainChanged as never);
          chainChanged = undefined;
        }
        if (disconnect) {
          provider.removeListener("disconnect", disconnect as never);
          disconnect = undefined;
        }
      },

      /**
       * Returns the list of currently connected account addresses.
       *
       * Delegates to `eth_accounts` on the underlying provider, which reads from
       * localStorage without opening any dialogs.
       *
       * @returns Array of connected addresses (empty if not connected)
       */
      async getAccounts() {
        const provider = await this.getProvider();
        return (await provider.request({
          method: "eth_accounts",
        })) as Address[];
      },

      /**
       * Returns the current chain ID as a number.
       *
       * Calls `eth_chainId` on the underlying provider and converts the hex result.
       *
       * @returns The numeric chain ID
       */
      async getChainId() {
        const provider = await this.getProvider();
        const hexChainId = (await provider.request({
          method: "eth_chainId",
        })) as string;
        return Number.parseInt(hexChainId, 16);
      },

      /**
       * Returns (and lazily creates) the underlying `OneAuthProvider` instance.
       *
       * The provider is a singleton per connector instance — created once and reused.
       * If a `chainId` argument is provided the provider switches to that chain before
       * returning.
       *
       * @param params.chainId - Optional chain ID to switch to before returning
       * @returns The singleton `OneAuthProvider`
       * @throws If no initial chain ID is configured
       */
      async getProvider({ chainId } = {}) {
        if (!provider) {
          if (!initialChainId) {
            throw new Error("No chain configured for 1auth connector");
          }
          provider = createOneAuthProvider({
            client: options.client,
            chainId: initialChainId,
            storageKey: options.storageKey,
            waitForHash: options.waitForHash,
            hashTimeoutMs: options.hashTimeoutMs,
            hashIntervalMs: options.hashIntervalMs,
          });
        }
        if (chainId) {
          await provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: numberToHex(chainId) }],
          });
        }
        return provider;
      },

      /**
       * Registers the one-time `connect` event listener used during initial setup.
       *
       * Called by wagmi during config initialization. Attaches `onConnect` to the
       * provider's `connect` event so that wagmi is notified if the user's session
       * is restored from localStorage before an explicit `connect()` call.
       */
      async setup() {
        const provider = await this.getProvider();
        if (!connect) {
          const onConnect = this.onConnect?.bind(this);
          if (onConnect) {
            connect = onConnect;
            provider.on("connect", connect as never);
          }
        }
      },

      /**
       * Returns whether the connector has a valid stored session.
       *
       * Used by wagmi on startup to decide whether to attempt auto-reconnect.
       * Returns false (rather than throwing) if account resolution fails.
       *
       * @returns True if at least one account address is available
       */
      async isAuthorized() {
        try {
          const accounts = await this.getAccounts();
          return accounts.length > 0;
        } catch {
          return false;
        }
      },

      /**
       * Switches the active chain on the underlying provider.
       *
       * Validates that the requested chain is present in the wagmi config before
       * issuing `wallet_switchEthereumChain` so that an informative error is thrown
       * rather than a silent failure.
       *
       * @param params.chainId - The numeric ID of the chain to switch to
       * @returns The `Chain` object from wagmi config for the new chain
       * @throws If the chain is not in the wagmi config's `chains` array
       */
      async switchChain({ chainId }) {
        const chain = chains.find((chain) => chain.id === chainId);
        if (!chain) {
          throw new Error("Chain not configured");
        }
        const provider = await this.getProvider();
        await provider.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: numberToHex(chainId) }],
        });
        return chain;
      },

      /**
       * Handles the provider `connect` event and forwards it to wagmi's emitter.
       *
       * Called once during the initial connection handshake. Resolves the current
       * accounts and emits a wagmi `connect` event with both accounts and chain ID.
       *
       * @param connectInfo - EIP-1193 connect info containing the hex chain ID
       */
      async onConnect(connectInfo: ProviderConnectInfo) {
        const accounts = await this.getAccounts();
        if (!accounts.length) return;
        const chainId = Number(connectInfo.chainId);
        wagmiConfig.emitter.emit("connect", { accounts, chainId });
      },

      /**
       * Handles the provider `accountsChanged` event and forwards it to wagmi.
       *
       * @param accounts - Updated list of account address strings
       */
      onAccountsChanged(accounts: string[]) {
        wagmiConfig.emitter.emit("change", {
          accounts: accounts as Address[],
        });
      },

      /**
       * Handles the provider `chainChanged` event and forwards it to wagmi.
       *
       * @param chainId - The new chain ID as a hex string or decimal string
       */
      onChainChanged(chainId: string) {
        wagmiConfig.emitter.emit("change", {
          chainId: Number(chainId),
        });
      },

      /**
       * Handles the provider `disconnect` event and forwards it to wagmi.
       *
       * @param _error - Optional error that caused the disconnection (unused)
       */
      onDisconnect(_error?: Error) {
        wagmiConfig.emitter.emit("disconnect");
      },
    };
  });
}
